import { useAtom, useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { gridColumnStateAtom, activeViewIdAtom, activeViewAtom, hasUnsavedChangesAtom, gridApiAtom } from '~/atoms/columnState';
import { createProject } from '~/service/savedViewService';
import { useSavedViews } from '~/atoms/hooks/useSavedViews';
import { captureGridState } from '~/utils/gridStateUtils';
import { linePlanEntityAtom } from '~/atoms/linePlanEntity';
import log from '~/logger';
// TODO: The functionality that uses this constant will need to be changed if we allow
// admin users or other users to save different view types like "Shared Views"
const SAVED_VIEW_DESCRIPTION = 'Favorites';
export const useColumnState = () => {
    const [gridColumnState, setGridColumnState] = useAtom(gridColumnStateAtom);
    const { savedViews, refreshSavedViews } = useSavedViews();
    const [activeViewId, setActiveViewId] = useAtom(activeViewIdAtom);
    const [activeView] = useAtom(activeViewAtom);
    const [hasUnsavedChanges] = useAtom(hasUnsavedChangesAtom);
    const gridApi = useAtomValue(gridApiAtom);
    const linePlanEntity = useAtomValue(linePlanEntityAtom);
    const captureFreshGridState = useCallback(() => {
        return captureGridState(gridApi);
    }, [gridApi]);
    const saveNewView = useCallback((viewName, projectId) => {
        if (!gridColumnState) {
            throw new Error('No grid state to save');
        }
        setActiveViewId(projectId);
        return projectId;
    }, [gridColumnState, setActiveViewId]);
    const saveNewViewAsync = useCallback(async (viewName, freshGridState) => {
        const stateToSave = freshGridState ?? captureFreshGridState() ?? gridColumnState;
        if (!stateToSave) {
            throw new Error('No grid state to save');
        }
        if (stateToSave !== gridColumnState) {
            setGridColumnState(stateToSave);
        }
        const projectData = {
            name: viewName.trim(),
            description: SAVED_VIEW_DESCRIPTION,
            metadata: stateToSave,
            status: true,
            namespace: process.env.REACT_APP_WORKSPACE_NAMESPACE ?? '',
            linePlanEntityType: linePlanEntity
        };
        try {
            const result = await createProject(projectData);
            if (result?.projectId) {
                return saveNewView(viewName.trim(), result.projectId);
            }
            else {
                throw new Error('No projectId returned from API');
            }
        }
        catch (error) {
            log.error('Failed to save view:', error);
            throw error;
        }
    }, [
        gridColumnState,
        saveNewView,
        captureFreshGridState,
        setGridColumnState,
        linePlanEntity
    ]);
    const updateSavedView = useCallback((freshGridState) => {
        const stateToSave = freshGridState ?? captureFreshGridState() ?? gridColumnState;
        if (!stateToSave) {
            throw new Error('No grid state to save');
        }
        if (stateToSave !== gridColumnState) {
            setGridColumnState(stateToSave);
        }
        refreshSavedViews();
    }, [
        gridColumnState,
        refreshSavedViews,
        captureFreshGridState,
        setGridColumnState
    ]);
    const loadSavedView = useCallback(async (viewId) => {
        const view = savedViews.find((v) => v.projectId === viewId);
        if (!view || !view.metadata) {
            throw new Error(`View with id ${viewId} not found or has no metadata`);
        }
        const gridState = view.metadata.gridState;
        setGridColumnState(gridState);
        setActiveViewId(viewId);
        return gridState;
    }, [setGridColumnState, setActiveViewId, savedViews]);
    return {
        gridColumnState,
        savedViews,
        activeViewId,
        activeView,
        hasUnsavedChanges,
        updateGridState: setGridColumnState,
        saveNewView,
        saveNewViewAsync,
        updateSavedView,
        loadSavedView
    };
};
